import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { FaArrowRight, FaBook, FaCalendar, FaUser } from "react-icons/fa";
import { colors, fonts } from "../styles/colors";
import { useExternalLink } from "../hooks/useExternalLink";

const HomeContainer = styled.div`
  padding-top: 80px;
`;

const HeroSection = styled.section`
  background: linear-gradient(rgba(26, 26, 26, 0.7), rgba(207, 169, 169, 0.8)),
    url("/images/home/download-4.jpg");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  color: white;
  padding: 6rem 2rem;
  text-align: center;
  min-height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1.5rem;
  font-weight: 600;
  font-family: ${fonts.heading};

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  margin-bottom: 2rem;
  opacity: 0.95;
  line-height: 1.6;
  font-family: ${fonts.body};
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: white;
  color: ${colors.inkBlack};
  padding: 1rem 2rem;
  border-radius: 50px;
  text-decoration: none;
  font-weight: bold;
  font-family: ${fonts.body};
  transition: all 0.3s ease;
  margin: 0 1rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
`;

const CTAButtonExternal = styled.button`
  display: inline-block;
  background: white;
  color: ${colors.inkBlack};
  padding: 1rem 2rem;
  border-radius: 50px;
  text-decoration: none;
  font-weight: bold;
  font-family: ${fonts.body};
  transition: all 0.3s ease;
  margin: 0 1rem;
  border: none;
  cursor: pointer;
  font-size: 1rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
`;

const SecondaryButton = styled(Link)`
  display: inline-block;
  border: 2px solid white;
  color: white;
  padding: 1rem 2rem;
  border-radius: 50px;
  text-decoration: none;
  font-weight: bold;
  font-family: ${fonts.body};
  transition: all 0.3s ease;
  margin: 0 1rem;

  &:hover {
    background: white;
    color: ${colors.inkBlack};
  }

  @media (max-width: 768px) {
    margin-top: 1rem;
  }
`;

const FeaturedSection = styled.section`
  padding: 5rem 2rem;
  background: white;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 3rem;
  color: ${colors.inkBlack};
  font-family: ${fonts.heading};
  font-weight: 600;
`;

const FeaturedGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 3rem;
`;

const FeaturedCard = styled.div`
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  text-align: center;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const CardIcon = styled.div`
  font-size: 3rem;
  color: ${colors.dustyRose};
  margin-bottom: 1rem;
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: ${colors.inkBlack};
  font-family: ${fonts.heading};
  font-weight: 500;
`;

const CardText = styled.p`
  color: ${colors.coolTaupe};
  line-height: 1.6;
  margin-bottom: 1.5rem;
  font-family: ${fonts.body};
  flex-grow: 1;
`;

const CardButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${colors.dustyRose};
  text-decoration: none;
  font-weight: bold;
  font-family: ${fonts.body};

  &:hover {
    text-decoration: underline;
  }
`;

const CardButtonExternal = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${colors.dustyRose};
  text-decoration: none;
  font-weight: bold;
  font-family: ${fonts.body};
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;

  &:hover {
    text-decoration: underline;
  }
`;

const PoemClubSection = styled.section`
  padding: 5rem 2rem;
  background: ${colors.gradient.primary};
  color: white;
`;

const PoemClubContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
`;

const PoemClubTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  font-family: ${fonts.heading};
  font-weight: 600;
`;

const PoemClubText = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  margin-bottom: 2rem;
  max-width: 880px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0.9;
  font-family: ${fonts.body};
`;

const PoemClubButton = styled.button`
  display: inline-block;
  background: white;
  color: ${colors.inkBlack};
  padding: 1rem 2rem;
  border-radius: 25px;
  text-decoration: none;
  font-weight: bold;
  font-family: ${fonts.body};
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  font-size: 1rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
`;

const AboutPreview = styled.section`
  padding: 3rem 2rem;
  background: ${colors.paleMauve};
`;

const AboutContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const AboutText = styled.div`
  h2 {
    font-size: 2.5rem;
    margin-bottom: 1rem;
    color: ${colors.inkBlack};
    font-family: ${fonts.heading};
    font-weight: 600;
  }

  p {
    font-size: 1.1rem;
    line-height: 1.5;
    color: ${colors.coolTaupe};
    margin-bottom: 1rem;
    font-family: ${fonts.body};
  }
`;

const AboutImage = styled.div`
  img {
    width: 100%;
    border-radius: 15px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ReviewsSection = styled.section`
  padding: 5rem 2rem;
  background: ${colors.paleMauve};

  @media (max-width: 768px) {
    padding: 3rem 1rem;
    text-align: center;
  }
`;

const ReviewsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 3rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 0;
    justify-items: center;
    max-width: 100%;
  }
`;

const ReviewCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  height: 100%;

  @media (max-width: 768px) {
    padding: 1.5rem;
    width: calc(100% - 2rem);
    max-width: 400px;
  }
`;

const ReviewStars = styled.div`
  color: ${colors.dustyRose};
  font-size: 1.2rem;
  margin-bottom: 1rem;
`;

const ReviewText = styled.p`
  color: ${colors.coolTaupe};
  line-height: 1.6;
  margin-bottom: 1.5rem;
  font-style: italic;
  font-family: ${fonts.body};
  flex-grow: 1;
`;

const ReviewAuthor = styled.div`
  border-top: 1px solid #eee;
  padding-top: 1rem;
`;

const ReviewName = styled.div`
  font-weight: bold;
  color: ${colors.inkBlack};
  margin-bottom: 0.25rem;
  font-family: ${fonts.body};
`;

const ReviewTitle = styled.div`
  color: ${colors.coolTaupe};
  font-size: 0.9rem;
  font-family: ${fonts.body};
`;

const RecentEventsSection = styled.section`
  padding: 5rem 2rem;
  background: white;
`;

const EventsGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
  margin-bottom: 3rem;
`;

const EventImageCard = styled.div`
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    height: 350px;
    object-fit: cover;
  }
`;

const EventsButton = styled(Link)`
  display: block;
  text-align: center;
  background: ${colors.dustyRose};
  color: white;
  padding: 1rem 2rem;
  border-radius: 25px;
  text-decoration: none;
  font-weight: bold;
  font-family: ${fonts.body};
  max-width: 200px;
  margin: 0 auto;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    background: ${colors.coolTaupe};
    transform: translateY(-2px);
  }
`;

const FeaturedInSection = styled.section`
  padding: 5rem 2rem;
  background: ${colors.paleMauve};
`;

const FeaturedLogos = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-around;
  align-items: center;
  flex-wrap: wrap;
  gap: 2rem;
  margin-top: 3rem;
`;

const LogoItem = styled.div`
  background: white;
  padding: 1rem 2rem;
  border-radius: 10px;
  font-weight: bold;
  color: ${colors.inkBlack};
  font-family: ${fonts.body};
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
`;

const ContactSection = styled.section`
  padding: 5rem 2rem;
  background: ${colors.gradient.primary};
  color: white;
`;

const ContactContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
`;

const ContactText = styled.div`
  h2 {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
    font-family: ${fonts.heading};
    font-weight: 600;
  }

  p {
    font-size: 1.1rem;
    line-height: 1.8;
    margin-bottom: 2rem;
    opacity: 0.9;
    font-family: ${fonts.body};
  }
`;

const ContactButton = styled(Link)`
  display: inline-block;
  background: white;
  color: ${colors.inkBlack};
  padding: 1rem 2rem;
  border-radius: 25px;
  text-decoration: none;
  font-weight: bold;
  font-family: ${fonts.body};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
`;

const SupportSection = styled.section`
  padding: 3rem 2rem;
  background: ${colors.boneWhite};
  text-align: center;
`;

const SupportContent = styled.div`
  max-width: 600px;
  margin: 0 auto;

  h3 {
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
    color: ${colors.inkBlack};
    font-family: ${fonts.heading};
    font-weight: 600;
  }
`;

const SupportButton = styled.a`
  display: inline-block;
  background: #ff5f5f;
  color: white;
  padding: 1rem 2rem;
  border-radius: 25px;
  text-decoration: none;
  font-weight: bold;
  font-family: ${fonts.body};
  transition: all 0.3s ease;

  &:hover {
    background: #e54d4d;
    transform: translateY(-2px);
  }
`;

const Home = () => {
  const handleExternalLink = useExternalLink();

  return (
    <HomeContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>Shana Marie Roark</HeroTitle>
          <HeroSubtitle>
            Poet, entrepreneur, and founder of Poem Club, based in New York City
          </HeroSubtitle>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              alignItems: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "1rem",
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <CTAButtonExternal
                  onClick={() =>
                    handleExternalLink(
                      "https://0c1c94-91.myshopify.com/products/custom-poems",
                      "Order Poem Online",
                      "hero_section"
                    )
                  }
                >
                  Order Poem Online
                </CTAButtonExternal>
                <div
                  style={{
                    marginTop: "0.5rem",
                    fontSize: "0.85rem",
                    opacity: 0.8,
                  }}
                >
                  Delivered to your door
                </div>
              </div>
              <div style={{ textAlign: "center" }}>
                <CTAButtonExternal
                  onClick={() =>
                    handleExternalLink(
                      "https://calendly.com/supergirlreject",
                      "Meet Shana in NYC",
                      "hero_section"
                    )
                  }
                >
                  Meet Shana in NYC
                </CTAButtonExternal>
                <div
                  style={{
                    marginTop: "0.5rem",
                    fontSize: "0.85rem",
                    opacity: 0.8,
                  }}
                >
                  Live sidewalk poetry experience
                </div>
              </div>
            </div>
          </div>
        </HeroContent>
      </HeroSection>

      <FeaturedSection>
        <SectionTitle>What Shana Does</SectionTitle>
        <FeaturedGrid>
          <FeaturedCard>
            <CardIcon>
              <FaBook />
            </CardIcon>
            <CardTitle>Poetry & Writing</CardTitle>
            <CardText>
              Shana crafts poems that explore themes of identity, love, loss,
              and healing. She also writes about her entrepreneurial journey to
              becoming a full-time poet.
            </CardText>
            <CardButton to="/books">
              Read Shana's Work <FaArrowRight />
            </CardButton>
          </FeaturedCard>

          <FeaturedCard>
            <CardIcon>
              <FaCalendar />
            </CardIcon>
            <CardTitle>Live Poetry at Events</CardTitle>
            <CardText>
              Shana brings a touch of timeless charm to your events with her
              vintage typewriter, crafting bespoke poems on the spot for your
              guests.
            </CardText>
            <CardButton to="/events">
              Book Your Event <FaArrowRight />
            </CardButton>
          </FeaturedCard>

          <FeaturedCard>
            <CardIcon>
              <FaUser />
            </CardIcon>
            <CardTitle>Bespoke Poetry</CardTitle>
            <CardText>
              Commission a one-of-a-kind poem crafted with your story in mind.
              Each piece is typed on vintage typewriter with imported Greek
              paper.
            </CardText>
            <CardButtonExternal
              onClick={() =>
                handleExternalLink(
                  "https://0c1c94-91.myshopify.com/products/custom-poems",
                  "Commission Your Poem",
                  "featured_section"
                )
              }
            >
              Commission Your Poem <FaArrowRight />
            </CardButtonExternal>
          </FeaturedCard>
        </FeaturedGrid>
      </FeaturedSection>

      <PoemClubSection>
        <PoemClubContent>
          <PoemClubTitle>Join the Poem Club</PoemClubTitle>
          <PoemClubText>
            Once a month, Shana sends you, or a loved one, a single
            poem—handcrafted with care, printed on paper you can hold, and
            wrapped in a letter written straight from her heart to yours.
          </PoemClubText>
          <PoemClubButton
            as="button"
            onClick={() =>
              handleExternalLink(
                "https://0c1c94-91.myshopify.com/products/poem-club",
                "Join the Poem Club",
                "poem_club_section"
              )
            }
          >
            Join the Poem Club
          </PoemClubButton>
        </PoemClubContent>
      </PoemClubSection>

      <AboutPreview>
        <AboutContent>
          <AboutText>
            <h2>About Shana</h2>
            <p>
              Shana's work has reached millions online, with over 70,000
              followers and videos viewed more than 10 million times. She’s been
              featured in{" "}
              <a
                href="https://nypost.com/2025/05/03/us-news/nyc-street-poet-left-teaching-to-pursue-dream-grateful-every-day/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i>The New York Post</i>
              </a>
              , which chronicled her journey to making a six-figure income from
              poetry.
            </p>
            <p>
              In 2019, she began Custom Poetry, a living conceptual art
              performance in which she sits on sidewalks and writes poems for
              strangers, on demand.
            </p>
            <p>Loneliness, she says, was the catalyst.</p>
            <p>
              She made a sign: <i>Pick a Subject, Get a Poem</i>.
            </p>
            <p>What happened next changed her life.</p>
            <CTAButton to="/about">Learn More About Her</CTAButton>
            <CTAButtonExternal
              onClick={() =>
                handleExternalLink(
                  "https://calendly.com/supergirlreject",
                  "Book a Consultation",
                  "about_preview_section"
                )
              }
              style={{ marginTop: "1rem" }}
            >
              Book a Consultation
            </CTAButtonExternal>
          </AboutText>
          <AboutImage>
            <img src="/images/home/about.jpg" alt="Shana Marie Roark" />
          </AboutImage>
        </AboutContent>
      </AboutPreview>

      <ReviewsSection>
        <SectionTitle>Kind Words</SectionTitle>
        <ReviewsGrid>
          <ReviewCard>
            <ReviewStars>★★★★★</ReviewStars>
            <ReviewText>
              "Art Basel is a really competitive space, and Shana was
              incredible. Each person left with a unique souvenir of the
              occasion: A one-of-a-kind poem from Shana. And they couldn't have
              been happier! Her words are really touching, and she is great with
              people, able to make them laugh, smile, and enjoy the unique
              experience of having a custom poem written for them."
            </ReviewText>
            <ReviewAuthor>
              <ReviewName>Marcia Lorente</ReviewName>
              <ReviewTitle>Founder of Surfergirl Gallery</ReviewTitle>
            </ReviewAuthor>
          </ReviewCard>

          <ReviewCard>
            <ReviewStars>★★★★★</ReviewStars>
            <ReviewText>
              "If you want people to remember your event, book Shana. She
              arrived on time, was extremely professional, and went above and
              beyond to make sure our guests had a great time. Not only does she
              give them poems, which can be branded with a logo or image, she
              gave them an experience."
            </ReviewText>
            <ReviewAuthor>
              <ReviewName>Daniel A.</ReviewName>
              <ReviewTitle>Event Client</ReviewTitle>
            </ReviewAuthor>
          </ReviewCard>

          <ReviewCard>
            <ReviewStars>★★★★★</ReviewStars>
            <ReviewText>
              "Shana was phenomenal. She made our engagement party one of a kind
              with her warm presence and beautiful poetry! Our guests couldn't
              stop talking about her poems."
            </ReviewText>
            <ReviewAuthor>
              <ReviewName>Christine C.</ReviewName>
              <ReviewTitle>Engagement Party Client</ReviewTitle>
            </ReviewAuthor>
          </ReviewCard>
        </ReviewsGrid>
      </ReviewsSection>

      <RecentEventsSection>
        <SectionTitle>Recent Live Poetry Events</SectionTitle>
        <EventsGrid>
          <EventImageCard>
            <img src="/images/home/r1.jpg" alt="Recent Event" />
          </EventImageCard>
          <EventImageCard>
            <img src="/images/home/r2.jpg" alt="Recent Event" />
          </EventImageCard>
          <EventImageCard>
            <img src="/images/home/r3.jpg" alt="Recent Event" />
          </EventImageCard>
        </EventsGrid>
        <EventsButton to="/events">Book Your Event →</EventsButton>
      </RecentEventsSection>

      <FeaturedInSection>
        <SectionTitle>As Featured In</SectionTitle>
        <FeaturedLogos>
          <LogoItem>Art Basel</LogoItem>
          <LogoItem>Gallery MC</LogoItem>
          <LogoItem>EDC New York</LogoItem>
          <LogoItem>Surfergirl Gallery</LogoItem>
          <LogoItem>Whitney Biennial</LogoItem>
        </FeaturedLogos>
      </FeaturedInSection>

      <ContactSection>
        <ContactContent>
          <ContactText>
            <h2>Get in Touch</h2>
            <p>
              Looking to commission a bespoke poem for someone you love? Want to
              book Shana's live poetry service for your event? Reach out and
              we'll get something going!
            </p>
            <ContactButton to="/contact">Contact Shana →</ContactButton>
          </ContactText>
        </ContactContent>
      </ContactSection>

      <SupportSection>
        <SupportContent>
          <h3>Support Shana's Craft!</h3>
          <SupportButton
            href="https://www.buymeacoffee.com/SuperGirlReject"
            target="_blank"
            rel="noopener noreferrer"
          >
            Buy Her a Coffee
          </SupportButton>
        </SupportContent>
      </SupportSection>
    </HomeContainer>
  );
};

export default Home;
